import { useState, useRef, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography, IconButton, Button } from '@mui/material';
import { fetchWithAuth } from '../../../api/backendApi';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import CloseIcon from '@mui/icons-material/Close';

function downsampleToInt16(input, inputSampleRate, outputSampleRate) {
  if (!input || input.length === 0) return null;

  if (outputSampleRate === inputSampleRate) {
    const buffer = new Int16Array(input.length);
    for (let i = 0; i < input.length; i++) {
      let s = Math.max(-1, Math.min(1, input[i]));
      buffer[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
    }
    return buffer;
  }

  const sampleRateRatio = inputSampleRate / outputSampleRate;
  const newLength = Math.round(input.length / sampleRateRatio);
  const result = new Int16Array(newLength);
  let offsetResult = 0;
  let offsetBuffer = 0;

  while (offsetResult < newLength) {
    const nextOffsetBuffer = Math.round((offsetResult + 1) * sampleRateRatio);
    let accum = 0;
    let count = 0;

    for (let i = Math.floor(offsetBuffer); i < nextOffsetBuffer && i < input.length; i++) {
      accum += input[i];
      count++;
    }

    let sample = count > 0 ? accum / count : 0;
    sample = Math.max(-1, Math.min(1, sample));
    result[offsetResult] = sample < 0 ? sample * 0x8000 : sample * 0x7fff;

    offsetResult++;
    offsetBuffer = nextOffsetBuffer;
  }

  return result;
}

export default function RtpPagingDialog({ open, onClose, presetZoneId, presetZoneName, micDeviceId }) {
  const [multicastGroup, setMulticastGroup] = useState('');
  const [port, setPort] = useState('');
  const [ttl, setTtl] = useState('');
  const [agentId] = useState('default'); // Backend will auto-select an online agent
  const [agentHost, setAgentHost] = useState('');
  const [zoneId, setZoneId] = useState('');
  const [isPaging, setIsPaging] = useState(false);
  const [sessionStarting, setSessionStarting] = useState(false);
  const [status, setStatus] = useState('Idle');
  const [elapsedMs, setElapsedMs] = useState(0);
  const [micLabel, setMicLabel] = useState('');
  const wsRef = useRef(null);
  const audioContextRef = useRef(null);
  const processorRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const sessionIdRef = useRef(null);
  const timerRef = useRef(null);
  const lastStartedAtRef = useRef(0);

  useEffect(() => {
    if (presetZoneId) {
      setZoneId(presetZoneId);
    } else if (!open) {
      setZoneId('');
    }
  }, [presetZoneId, open]);

  useEffect(() => {
    if (isPaging) {
      const start = Date.now();
      setElapsedMs(0);
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      timerRef.current = setInterval(() => {
        setElapsedMs(Date.now() - start);
      }, 1000);
    } else if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isPaging]);

  const buildOverridePayload = () => {
    const override = {};
    if (multicastGroup.trim()) override.group = multicastGroup.trim();
    if (port.trim()) override.port = Number(port.trim());
    if (ttl.trim()) override.ttl = Number(ttl.trim());
    if (agentHost.trim()) override.agentHost = agentHost.trim();
    return override;
  };

  const stopAudio = () => {
    if (processorRef.current) {
      try {
        processorRef.current.disconnect();
      } catch (e) {
        console.error('[RtpPagingDialog] Error disconnecting audio processor', e);
      }
      processorRef.current.onaudioprocess = null;
      processorRef.current = null;
    }

    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => {
        try {
          track.stop();
        } catch (e) {
          console.error('[RtpPagingDialog] Error stopping media track', e);
        }
      });
      mediaStreamRef.current = null;
    }

    if (audioContextRef.current) {
      try {
        audioContextRef.current.close();
      } catch (e) {
        console.error('[RtpPagingDialog] Error closing AudioContext', e);
      }
      audioContextRef.current = null;
    }
  };

  const ensureMicrophoneAccess = async () => {
    const mediaApi = window.electronAPI?.media;
    if (!mediaApi?.requestMicrophoneAccess) {
      return true;
    }

    try {
      const current = await mediaApi.getMicrophoneAccessStatus?.();
      if (current?.status === 'granted') {
        return true;
      }

      const result = await mediaApi.requestMicrophoneAccess();
      if (result?.granted || result?.status === 'granted') {
        return true;
      }

      setStatus('Microphone permission denied. Enable microphone access for Signal Bridge and try again.');
      return false;
    } catch (err) {
      console.error('[RtpPagingDialog] Failed to request microphone access', err);
      return true;
    }
  };

  const startAudioCapture = async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      console.error('[RtpPagingDialog] getUserMedia is not supported in this browser');
      setStatus('Browser does not support microphone capture');
      return;
    }

    console.log('[RtpPagingDialog] Requesting microphone access...');
    try {
      const baseAudioConstraints = {
        channelCount: 1,
        echoCancellation: true,
        noiseSuppression: true,
      };

      let stream;
      try {
        if (micDeviceId) {
          stream = await navigator.mediaDevices.getUserMedia({
            audio: {
              ...baseAudioConstraints,
              deviceId: { exact: micDeviceId },
            },
          });
        } else {
          stream = await navigator.mediaDevices.getUserMedia({ audio: baseAudioConstraints });
        }
      } catch (primaryErr) {
        if (micDeviceId) {
          console.warn('[RtpPagingDialog] getUserMedia with specific device failed, falling back to default device', primaryErr);
          stream = await navigator.mediaDevices.getUserMedia({ audio: baseAudioConstraints });
        } else {
          throw primaryErr;
        }
      }
      mediaStreamRef.current = stream;

      // Update mic label from the actual stream
      const audioTrack = stream.getAudioTracks()[0];
      if (audioTrack) {
        const label = audioTrack.label || 'Default Microphone';
        setMicLabel(label);
        console.log('[RtpPagingDialog] Using microphone:', label);
      }

      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      let audioContext;
      try {
        audioContext = new AudioContextClass({ sampleRate: 8000 });
      } catch (sampleRateErr) {
        console.warn('[RtpPagingDialog] 8000 Hz AudioContext unavailable, using default rate', sampleRateErr);
        audioContext = new AudioContextClass();
      }
      audioContextRef.current = audioContext;

      const source = audioContext.createMediaStreamSource(stream);
      const processor = audioContext.createScriptProcessor(4096, 1, 1);
      processorRef.current = processor;
      const targetSampleRate = 8000;

      processor.onaudioprocess = (event) => {
        const channelData = event.inputBuffer.getChannelData(0);
        const ws = wsRef.current;
        if (!ws || ws.readyState !== WebSocket.OPEN) {
          return;
        }

        const pcm16 = downsampleToInt16(channelData, audioContext.sampleRate, targetSampleRate);
        if (pcm16 && ws.readyState === WebSocket.OPEN) {
          try {
            const start = pcm16.byteOffset;
            const end = start + pcm16.byteLength;
            const payload = start === 0 && end === pcm16.buffer.byteLength
              ? pcm16.buffer
              : pcm16.buffer.slice(start, end);
            ws.send(payload);
          } catch (e) {
            console.error('[RtpPagingDialog] Error sending audio chunk', e);
          }
        }
      };

      source.connect(processor);
      const silentSink = audioContext.createGain();
      silentSink.gain.value = 0;
      processor.connect(silentSink);
      silentSink.connect(audioContext.destination);
      console.log('[RtpPagingDialog] Microphone capture started successfully');
      setStatus('Streaming microphone audio to agent...');
    } catch (err) {
      console.error('[RtpPagingDialog] Microphone error', err);
      console.error('[RtpPagingDialog] Microphone error details:', {
        name: err?.name,
        message: err?.message,
        constraint: err?.constraint
      });
      if (err?.name === 'NotAllowedError') {
        setStatus('Microphone permission denied. Enable microphone access for Signal Bridge and try again.');
      } else {
        setStatus('Microphone error or permission denied');
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      setIsPaging(false);
    }
  };

  const handleStartStop = async () => {
    if (!isPaging) {
      try {
        setSessionStarting(true);
        const hasMicAccess = await ensureMicrophoneAccess();
        if (!hasMicAccess) {
          setSessionStarting(false);
          return;
        }
        if (!zoneId.trim()) {
          setStatus('A paging zone is required (configure zones in Admin > Tenants > Paging)');
          setSessionStarting(false);
          return;
        }
        setStatus('Requesting paging session from backend...');
        console.log('[RtpPagingDialog] Calling backend to start paging session');
        const body = {
          target: agentId || 'default',
          zoneId: zoneId.trim(),
          override: buildOverridePayload(),
        };
        const resp = await fetchWithAuth('/api/paging/session/start', 'POST', body);
        if (!resp?.success) {
          console.error('[RtpPagingDialog] Backend paging start failed', resp);
          setStatus(resp?.error || 'Backend paging start failed');
          return;
        }
        const { sessionId, wsUrl } = resp;
        sessionIdRef.current = sessionId;
        console.log('[RtpPagingDialog] Backend returned session', { sessionId, wsUrl });

        const socket = new WebSocket(wsUrl);
        wsRef.current = socket;
        setStatus('Connecting to agent...');
        console.log('[RtpPagingDialog] Opening WebSocket', wsUrl);

        socket.onopen = () => {
          console.log('[RtpPagingDialog] WebSocket connected');
          lastStartedAtRef.current = Date.now();
          setIsPaging(true);
          setSessionStarting(false);
          setStatus('Connected; starting microphone...');
          startAudioCapture();
        };

        socket.onerror = (event) => {
          console.error('[RtpPagingDialog] WebSocket error', event);
          setSessionStarting(false);
          setStatus('Error connecting to agent');
        };

        socket.onclose = (event) => {
          const closedAt = Date.now();
          const startedAt = lastStartedAtRef.current || 0;
          const liveMs = startedAt > 0 ? Math.max(0, closedAt - startedAt) : 0;
          const closeDetails = {
            sessionId: sessionIdRef.current,
            code: event.code,
            reason: event.reason || '(none)',
            wasClean: event.wasClean,
            readyState: socket.readyState,
            liveMs,
          };
          console.warn('[RtpPagingDialog] WebSocket closed', closeDetails);
          stopAudio();
          wsRef.current = null;
          setIsPaging(false);
          setSessionStarting(false);
          const reasonText = event.reason ? ` (${event.reason})` : '';
          setStatus(`Disconnected (code ${event.code})${reasonText}`);
        };
      } catch (err) {
        console.error('[RtpPagingDialog] Failed to start paging session via backend', err);
        setSessionStarting(false);
        setStatus(err?.message || 'Failed to start paging session');
      }
    } else {
      if (sessionStarting) {
        console.log('[RtpPagingDialog] Ignoring stop request while session is starting');
        return;
      }

      const startedAt = lastStartedAtRef.current;
      if (startedAt && Date.now() - startedAt < 1000) {
        console.log('[RtpPagingDialog] Ignoring stop request within start grace period');
        return;
      }

      if (wsRef.current) {
        try {
          wsRef.current.close();
        } catch (e) {
          console.error('[RtpPagingDialog] Error closing WebSocket', e);
        }
        wsRef.current = null;
      }
      stopAudio();
      setIsPaging(false);
      setStatus('Stopped');
      console.log('[RtpPagingDialog] Stop paging');

      const sid = sessionIdRef.current;
      sessionIdRef.current = null;
      if (sid) {
        try {
          await fetchWithAuth('/api/paging/session/stop', 'POST', { sessionId: sid }, { silent: true });
          console.log('[RtpPagingDialog] Notified backend to stop session', sid);
        } catch (e) {
          console.warn('[RtpPagingDialog] Failed to notify backend to stop session', { sessionId: sid, error: e?.message });
        }
      }
    }
  };

  const handleClose = async () => {
    if (wsRef.current) {
      try {
        wsRef.current.close();
      } catch (e) {
        console.error('[RtpPagingDialog] Error closing WebSocket on dialog close', e);
      }
      wsRef.current = null;
    }
    stopAudio();
    setIsPaging(false);
    setStatus('Idle');

    const sid = sessionIdRef.current;
    sessionIdRef.current = null;
    if (sid) {
      try {
        await fetchWithAuth('/api/paging/session/stop', 'POST', { sessionId: sid }, { silent: true });
        console.log('[RtpPagingDialog] Notified backend to stop session on close', sid);
      } catch (e) {
        console.warn('[RtpPagingDialog] Failed to notify backend to stop session on close', { sessionId: sid, error: e?.message });
      }
    }

    setElapsedMs(0);
    setMicLabel('');
    onClose?.();
  };

  const totalSeconds = Math.floor(elapsedMs / 1000);
  const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, '0');
  const seconds = String(totalSeconds % 60).padStart(2, '0');
  const timerLabel = `${minutes}:${seconds}`;

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        console.log('[RtpPagingDialog] Dialog onClose', { reason, isPaging, sessionStarting });
        if ((isPaging || sessionStarting) && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
          return;
        }
        handleClose();
      }}
      disableEscapeKeyDown={isPaging || sessionStarting}
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" component="span">Live Paging (RTP)</Typography>
        <IconButton size="small" onClick={handleClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {presetZoneId ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Zone
              </Typography>
              <Typography variant="body2" color="text.primary">
                {presetZoneName || 'Paging zone'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Paging zone configured by your administrator
              </Typography>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No paging zone is set on this button.
            </Typography>
          )}

          <Typography variant="body2" color="text.secondary">
            {isPaging
              ? 'Tap the mic to stop paging. Your microphone is live to this zone.'
              : 'Tap the mic to start paging. Your microphone audio will be sent to this zone.'}
          </Typography>

          {micLabel && (
            <Typography variant="caption" color="text.secondary">
              Using: {micLabel}
            </Typography>
          )}

          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
            <IconButton
              onClick={handleStartStop}
              disabled={sessionStarting || (isPaging && lastStartedAtRef.current && Date.now() - lastStartedAtRef.current < 1000)}
              color={isPaging ? 'error' : 'primary'}
              sx={{
                width: 88,
                height: 88,
                borderRadius: '50%',
                boxShadow: 4,
              }}
            >
              {isPaging ? <StopIcon sx={{ fontSize: 40 }} /> : <MicIcon sx={{ fontSize: 40 }} />}
            </IconButton>
            <Typography
              variant="h6"
              sx={{ mt: 0.5, fontVariantNumeric: 'tabular-nums' }}
            >
              {timerLabel}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {status}
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
